import { useState, useEffect, useMemo } from "react";
import { Svg_10, Svg_11, Svg_12 } from "../Icons/Icons";

export const TicketProgressBar = ({
  authToken,
  ticketId,
  ticket,
  isPartner,
}) => {
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/API/V2/Tickets/${ticketId}/history`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      },
    )
      .then((res) => res.json())
      .then((data) => setHistory(Array.isArray(data) ? data : []));
  }, [authToken, ticketId]);

  const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const amPm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${amPm}, ${day}-${month}-${year}`;
  };

  const findHistoryByNewValue = useMemo(() => {
    const map = {};
    ["Reopen", "Active", "Resolved", "Closed"].forEach((val) => {
      const entry = history.find((e) =>
        e.changes?.some((c) => c.newValue === val),
      );
      map[val] = entry;
    });
    return (val) => map[val];
  }, [history]);

  let a = 0,
    b = 0,
    c = 0,
    d = 0;
  if (ticket.status == 1 || ticket.status == 6) {
    a = 1;
  } else if (ticket.status == 2) {
    a = 1;
    b = 1;
  } else if (ticket.status == 3) {
    a = 1;
    b = 1;
    c = 1;
  } else if (ticket.status == 4) {
    a = 1;
    b = 1;
    c = 1;
    d = 1;
  }

  const toggleExpand = () => setExpanded((prev) => !prev);

  const stepActiveClass = "bg-jiojbm30 fill-jiojbm50";
  const stepInactiveClass = "bg-gray-100 fill-gray-400";
  const lineActiveClass = "bg-jiojbm30";
  const lineInactiveClass = "bg-gray-100";
  const textActiveClass = "text-jiojbm50";
  const textInactiveClass = "text-gray-400";

  return (
    <div className="flex items-center overflow-hidden">
      <div className="flex items-center">
        <button
          type="button"
          onClick={toggleExpand}
          className="flex items-center justify-center w-10 h-10 rounded-full cursor-pointer fill-jiojbm50 hover:bg-jiojbm20 transition-all"
          aria-label={expanded ? "Collapse steps" : "Expand steps"}
        >
          <svg
            viewBox="0 0 24 24"
            className={`w-6 h-6 transition-transform duration-300 ${
              expanded ? "rotate-180" : ""
            }`}
          >
            <path d="M14 17a1.003 1.003 0 01-.71-.29l-4-4a1 1 0 010-1.42l4-4a1.005 1.005 0 011.42 1.42L11.41 12l3.3 3.29a.997.997 0 01.219 1.095.999.999 0 01-.93.615z" />
          </svg>
        </button>
      </div>

      <div className="">
        <ol
          className={`flex flex-col relative group transition-all duration-300 pr-8 ${
            expanded ? "w-72 items-start" : "w-fit items-center"
          }`}
        >
          {/* STEP 1 — OPEN */}
          <li className="flex flex-row gap-4 items-center min-h-[12vh]">
            <div className="flex flex-col items-center">
              <div className="flex-grow w-1 bg-transparent" />
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                  a === 1 ? stepActiveClass : stepInactiveClass
                }`}
              >
                <Svg_10 />
              </span>
              <div
                className={`w-1 h-[10vh] ${a === 1 ? lineActiveClass : lineInactiveClass}`}
              />
            </div>
            {expanded && (
              <div className="bg-white rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.24)] p-4 py-3 w-48 mb-auto">
                <p
                  className={`text-xs font-bold text-center italic ${
                    a === 1 ? textActiveClass : textInactiveClass
                  }`}
                >
                  Open
                </p>
                <p
                  className={`text-xs italic text-center ${
                    a === 1 ? textActiveClass : textInactiveClass
                  }`}
                >
                  {findHistoryByNewValue("Reopen") ? (
                    <>
                      {!isPartner && (
                        <>
                          Reopened by{" "}
                          {findHistoryByNewValue("Reopen")?.changedBy}
                          <br />
                        </>
                      )}
                      on{" "}
                      {formatDate(findHistoryByNewValue("Reopen")?.timestamp)}
                    </>
                  ) : (
                    <>
                      {!isPartner && (
                        <>
                          Created by {ticket.createdBy}
                          <br />
                        </>
                      )}
                      on {formatDate(ticket.createdAt)}.
                    </>
                  )}
                </p>
              </div>
            )}
          </li>

          {/* STEP 2 — ACTIVE */}
          <li className="flex flex-row gap-4 items-center min-h-[12vh]">
            <div className="flex flex-col items-center">
              <div
                className={`w-1 h-[10vh] ${b === 1 ? lineActiveClass : lineInactiveClass}`}
              />
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                  b === 1 ? stepActiveClass : stepInactiveClass
                }`}
              >
                <Svg_11 />
              </span>
              <div
                className={`w-1 h-[10vh] ${b === 1 ? lineActiveClass : lineInactiveClass}`}
              />
            </div>
            {expanded && (
              <div className="bg-white rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.24)] p-4 py-3 w-48">
                <p
                  className={`text-xs font-bold text-center italic ${
                    b === 1 ? textActiveClass : textInactiveClass
                  }`}
                >
                  Active
                </p>
                <p
                  className={`text-xs italic text-center ${
                    b === 1 ? textActiveClass : textInactiveClass
                  }`}
                >
                  {b === 1 ? (
                    <>
                      {!isPartner && (
                        <>
                          Updated by{" "}
                          {findHistoryByNewValue("Active")?.changedBy}
                          <br />
                        </>
                      )}
                      on{" "}
                      {formatDate(findHistoryByNewValue("Active")?.timestamp)}
                    </>
                  ) : (
                    <>
                      {!isPartner && (
                        <>
                          Pending with {ticket.assign}
                          <br />
                          from {formatDate(ticket.createdAt)}.
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>
            )}
          </li>

          {/* STEP 3 — RESOLVED */}
          <li className="flex flex-row gap-4 items-center min-h-[12vh]">
            <div className="flex flex-col items-center">
              <div
                className={`w-1 h-[10vh] ${c === 1 ? lineActiveClass : lineInactiveClass}`}
              />
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                  c === 1 ? stepActiveClass : stepInactiveClass
                }`}
              >
                <Svg_12 />
              </span>
              <div
                className={`w-1 h-[10vh] ${c === 1 ? lineActiveClass : lineInactiveClass}`}
              />
            </div>
            {expanded && (
              <div className="bg-white rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.24)] p-4 py-3 w-48">
                <p
                  className={`text-xs font-bold text-center italic ${
                    c === 1 ? textActiveClass : textInactiveClass
                  }`}
                >
                  Resolved
                </p>
                <p
                  className={`text-xs italic text-center ${
                    c === 1 ? textActiveClass : textInactiveClass
                  }`}
                >
                  {a === 1 && b === 1 && c === 0 && (
                    <>
                      {!isPartner && (
                        <>
                          Pending with {ticket.assign}
                          <br />
                          from {formatDate(ticket.createdAt)}.
                        </>
                      )}
                    </>
                  )}
                  {c === 1 && (
                    <>
                      {!isPartner && (
                        <>
                          Updated by{" "}
                          {findHistoryByNewValue("Resolved")?.changedBy}
                          <br />
                        </>
                      )}
                      on{" "}
                      {formatDate(findHistoryByNewValue("Resolved")?.timestamp)}
                    </>
                  )}
                </p>
              </div>
            )}
          </li>

          {/* STEP 4 — CLOSED */}
          <li className="flex flex-row gap-4 items-center min-h-[12vh]">
            <div className="flex flex-col items-center">
              <div
                className={`w-1 h-[10vh] ${d === 1 ? lineActiveClass : lineInactiveClass}`}
              />
              <span
                className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                  d === 1 ? stepActiveClass : stepInactiveClass
                }`}
              >
                <svg
                  version="1.0"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-5 h-5 ml-0.5"
                  viewBox="0 0 900.000000 816.000000"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <g
                    transform="translate(0.000000,816.000000) scale(0.100000,-0.100000)"
                    stroke="none"
                  >
                    <path d="M3990 7730 c-82 -8 -209 -50 -278 -94 -23 -14 -106 -81 -183 -149 -78 -67 -219 -189 -313 -270 -132 -114 -189 -157 -251 -187 -158 -77 -127 -73 -585 -80 -380 -7 -422 -9 -495 -29 -128 -34 -219 -85 -306 -171 -92 -92 -138 -161 -175 -265 l-28 -80 -7 -410 c-7 -439 -8 -450 -64 -590 -33 -83 -95 -174 -213 -311 -344 -400 -423 -501 -454 -579 -43 -106 -53 -160 -53 -280 0 -120 11 -174 53 -280 14 -36 78 -142 92 -155 3 -3 55 -63 115 -135 60 -71 127 -151 149 -176 23 -25 50 -56 60 -70 11 -13 45 -53 76 -89 82 -92 142 -184 174 -265 57 -140 58 -150 65 -590 l7 -410 28 -80 c76 -215 252 -374 481 -436 73 -20 113 -22 490 -29 449 -7 435 -5 576 -74 100 -49 123 -67 504 -401 234 -204 267 -227 395 -273 89 -32 103 -34 220 -35 115 -1 133 2 224 32 117 39 195 85 286 171 9 8 38 33 65 55 27 22 69 59 94 81 128 116 371 319 408 341 23 13 82 41 130 61 l88 37 410 6 c378 6 416 8 490 28 201 54 352 176 443 357 66 131 65 122 73 569 7 389 12 467 36 525 6 14 19 45 29 70 35 86 80 147 288 390 116 135 213 247 216 250 4 3 28 31 53 62 114 140 166 284 167 460 0 104 -9 154 -46 258 -35 96 -75 150 -332 448 -308 357 -320 373 -367 502 -34 93 -38 134 -44 530 -6 352 -9 413 -25 465 -80 262 -258 429 -526 493 -50 12 -155 17 -465 21 l-400 6 -70 28 c-143 58 -188 88 -360 237 -55 48 -113 99 -130 113 -16 14 -91 78 -165 143 -141 122 -221 180 -280 202 -136 51 -230 64 -370 52z m1480 -2420 c39 -20 85 -72 114 -127 24 -48 22 -161 -6 -215 -27 -55 -1732 -1766 -1797 -1805 -39 -22 -56 -26 -128 -26 -80 0 -86 2 -136 36 -55 39 -876 858 -918 917 -92 129 -43 317 99 380 58 26 163 28 219 5 26 -11 145 -122 383 -360 190 -190 350 -345 355 -345 5 0 359 348 785 774 725 724 778 775 820 785 61 14 164 4 210 -19z" />
                  </g>
                </svg>
              </span>
              <div className="w-1 flex-grow bg-transparent" />
            </div>
            {expanded && (
              <div className="bg-white rounded-xl shadow-[0_4px_16px_rgba(0,0,0,0.24)] p-4 py-3 w-48 h-fit mt-auto">
                <p
                  className={`text-xs font-bold text-center italic ${
                    d === 1 ? textActiveClass : textInactiveClass
                  }`}
                >
                  Closed
                </p>
                <p
                  className={`text-xs italic text-center ${
                    d === 1 ? textActiveClass : textInactiveClass
                  }`}
                >
                  {a === 1 && b === 1 && c === 1 && d === 0 && (
                    <>
                      {!isPartner && (
                        <>
                          Pending with {ticket.createdBy}
                          <br />
                          from{" "}
                          {formatDate(
                            findHistoryByNewValue("Resolved")?.timestamp,
                          )}
                          .
                        </>
                      )}
                    </>
                  )}
                  {d === 1 && (
                    <>
                      {!isPartner && (
                        <>
                          Updated by{" "}
                          {findHistoryByNewValue("Closed")?.changedBy}
                          <br />
                        </>
                      )}
                      on{" "}
                      {formatDate(findHistoryByNewValue("Closed")?.timestamp)}
                    </>
                  )}
                </p>
              </div>
            )}
          </li>
        </ol>
      </div>
    </div>
  );
};

export default TicketProgressBar;
